import { lazy, Suspense, useEffect } from "react";
import { createBrowserRouter, Link, Navigate, Outlet } from "react-router-dom";
import App from "./App";
import { ROUTES } from "./routes";

// Pages
import AppLayout from "./layouts/AppLayout";
import DashboardPageNotFound from "./layouts/DashboardPageNotFound";
import PageNotFound from "./components/layout/PageNotFound";
import Login from "./pages/auth/login";
import { Logout } from "./pages/auth/Logout";
import OTPVerification from "./pages/auth/verifyOtp";
import ForgotPassword from "./pages/auth/forgotPassword";
import RegionSelectionPage from "./pages/auth/RegionSelect";
import DefaultPasswordSelectPage from "./pages/auth/DefaultPasswordSelect";

// cards
import DashboardPage from "./pages/dashboard/index";
import CardsPage from "./pages/cards/cards";

// reports
import ReportingIndexPage from "./pages/reports";
import ReportingCardLoadPage from "./pages/reports/ReportsCardLoad";
import ReportingPrefundPage from "./pages/reports/ReportsPrefund";
import ReportingLedgerPage from "./pages/reports/ReportsLedger";
import ReportingB2BPage from "./pages/reports/ReportsB2B";
import ReportingDriverPaymentReqPage from "./pages/reports/ReportsDriverPaymentReq";
import SkeletonPage from "./components/skeletons/SkeletonPage";
import { Spinner } from "./components/ui/loader";

import PrivateRoute from "./components/permission/PrivateRoute";
import { APP_USERS } from "./consts/AppRoles";

const DriversPage = lazy(() => import("./pages/drivers"));

// requests
const RequestsIndexPage = lazy(() => import("./pages/requests"));
const RequestsCardLoadPage = lazy(() =>
  import("./pages/requests/RequestCardLoad")
);
const RequestsPrefundPage = lazy(() =>
  import("./pages/requests/RequestPrefund")
);
const RequestsB2BPage = lazy(() => import("./pages/requests/RequestB2B"));
const RequestsDriverPaymentReqPage = lazy(() =>
  import("./pages/requests/RequestDriverPaymentReq")
);
const RequestsExpenseManagementPage = lazy(() =>
  import("./pages/requests/RequestExpenseManagement")
);

// settings
const SettingsIndexPage = lazy(() => import("./pages/settings/index"));
const SettingsUsersPage = lazy(() => import("./pages/settings/Users"));
const SettingsHelpPage = lazy(() => import("./pages/settings/Help"));
const SettingsSecurityPage = lazy(() => import("./pages/settings/Security"));
const SettingsAccountsPage = lazy(() =>
  import("./pages/settings/AccountDetails")
);
const SettingsUsageReportPage = lazy(() =>
  import("./pages/settings/UsageReport")
);

// Store
const StoreIndexPage = lazy(() => import("./pages/store"));
const StoreHomePage = lazy(() => import("./pages/store/Home"));
const StoreProductPage = lazy(() => import("./pages/store/ProductDetailPage"));
const StoreCartPage = lazy(() => import("./pages/store/cart/Cart"));
const OrderDetailPage = lazy(() => import("./pages/store/OrderDetail"));
const OrderHistoryPage = lazy(() => import("./pages/store/OrderHistory"));
const StoreProductsPage = lazy(() => import("./pages/store/Products"));

// Profile
const ProfilePage = lazy(() => import("./pages/profile"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));

// Bulk Operations
const BulkOperationIndexPage = lazy(() => import("./pages/bulk/index"));
const BulkOperationLoadCardPage = lazy(() => import("./pages/bulk/LoadCard"));
const BulkOperationLoadDriverPage = lazy(() =>
  import("./pages/bulk/LoadDriver")
);
const BulkOperationCardSummaryPage = lazy(() =>
  import("./pages/bulk/CardSummary")
);
const BulkOperationDriverSummaryPage = lazy(() =>
  import("./pages/bulk/DriverSummary")
);

// wps
export const WpsPage = lazy(() => import("./pages/wps"));
export const EmployeePage = lazy(() => import("./pages/wps/Employee"));
export const RecentlyPaidSalaryPage = lazy(() =>
  import("./pages/wps/RecentlyPaidSalary")
);
export const EmployerGenerateSalaryPage = lazy(() =>
  import("./pages/wps/EmployerGenerateSalary")
);

const browserRouter = createBrowserRouter([
  {
    path: ROUTES.BASE,
    element: <Navigate to={ROUTES.APP_BASE} />,
    errorElement: <PageNotFound />,
  },

  {
    path: ROUTES.APP_BASE,
    element: <App />,
  },

  // Auth Routes
  {
    path: ROUTES.LOGIN,
    element: <Login />,
  },
  {
    path: ROUTES.LOGOUT,
    element: <Logout />,
  },
  {
    path: ROUTES.VERIFY_OTP,
    element: <OTPVerification />,
  },
  {
    path: ROUTES.FORGOT_PASSWORD,
    element: <ForgotPassword />,
  },
  {
    path: ROUTES.REGION,
    element: <RegionSelectionPage />,
  },
  {
    path: ROUTES.DEFAULT_PASSWORD,
    element: <DefaultPasswordSelectPage />,
  },
  {
    path: ROUTES.RESET_PASSWORD,
    element: (
      <Suspense fallback={<Spinner text={"Please wait.."} />}>
        <ResetPassword />
      </Suspense>
    ),
  },

  // dashboard routes
  {
    path: ROUTES.DASHBOARD_BASE,
    element: <AppLayout />,
    errorElement: <DashboardPageNotFound />,
    children: [
      {
        path: ROUTES.DASHBOARD,
        element: (
          <PrivateRoute
            allowedRoles={[
              APP_USERS.CLIENT,
              APP_USERS.SUPER_DISTRIBUTOR,
              APP_USERS.DISTRIBUTOR,
              APP_USERS.AGENT,
            ]}
          >
            <DashboardPage />
          </PrivateRoute>
        ),
        children: [],
      },
      {
        path: ROUTES.PROFILE,
        element: (
          <Suspense fallback={<Spinner text={"Loading.."} />}>
            <PrivateRoute
              allowedRoles={[
                APP_USERS.CLIENT,
                APP_USERS.SUPER_DISTRIBUTOR,
                APP_USERS.DISTRIBUTOR,
                APP_USERS.AGENT,
              ]}
            >
              <ProfilePage />
            </PrivateRoute>
          </Suspense>
        ),
        children: [],
      },
      {
        path: ROUTES.CARDS.INDEX,
        element: (
          <PrivateRoute
            allowedRoles={[
              APP_USERS.CLIENT,
              APP_USERS.SUPER_DISTRIBUTOR,
              APP_USERS.DISTRIBUTOR,
              APP_USERS.AGENT,
            ]}
          >
            <CardsPage />
          </PrivateRoute>
        ),
        children: [],
      },
      {
        path: ROUTES.DRIVERS.INDEX,
        element: (
          <Suspense fallback={<Spinner text={"Loading.."} />}>
            <PrivateRoute
              allowedRoles={[
                APP_USERS.CLIENT,
                APP_USERS.SUPER_DISTRIBUTOR,
                APP_USERS.DISTRIBUTOR,
                APP_USERS.AGENT,
              ]}
            >
              <DriversPage />
            </PrivateRoute>
          </Suspense>
        ),
        children: [],
      },

      {
        path: ROUTES.DRIVERS.INDEX,
        element: (
          <Suspense fallback={<Spinner text={"Loading.."} />}>
            <PrivateRoute
              allowedRoles={[
                APP_USERS.CLIENT,
                APP_USERS.SUPER_DISTRIBUTOR,
                APP_USERS.DISTRIBUTOR,
                APP_USERS.AGENT,
              ]}
            >
              <DriversPage />
            </PrivateRoute>
          </Suspense>
        ),
        children: [],
      },

      // Reporting
      {
        path: ROUTES.BULK_OPERAION.INDEX,
        element: (
          <Suspense fallback={<SkeletonPage />}>
            <PrivateRoute
              allowedRoles={[
                APP_USERS.CLIENT,
                APP_USERS.SUPER_DISTRIBUTOR,
                APP_USERS.DISTRIBUTOR,
                APP_USERS.AGENT,
              ]}
            >
              <BulkOperationIndexPage />
            </PrivateRoute>
          </Suspense>
        ),
        children: [
          {
            path: ROUTES.BULK_OPERAION.LOAD_CARD,
            element: (
              <PrivateRoute
                allowedRoles={[
                  APP_USERS.CLIENT,
                  APP_USERS.SUPER_DISTRIBUTOR,
                  APP_USERS.DISTRIBUTOR,
                  APP_USERS.AGENT,
                ]}
              >
                <BulkOperationLoadCardPage />
              </PrivateRoute>
            ),
          },
          {
            path: ROUTES.BULK_OPERAION.LOAD_DRIVER,
            element: (
              <PrivateRoute
                allowedRoles={[
                  APP_USERS.CLIENT,
                  APP_USERS.SUPER_DISTRIBUTOR,
                  APP_USERS.DISTRIBUTOR,
                  APP_USERS.AGENT,
                ]}
              >
                <BulkOperationLoadDriverPage />
              </PrivateRoute>
            ),
          },
          {
            path: ROUTES.BULK_OPERAION.CARD_SUMMARY,
            element: (
              <PrivateRoute
                allowedRoles={[
                  APP_USERS.CLIENT,
                  APP_USERS.SUPER_DISTRIBUTOR,
                  APP_USERS.DISTRIBUTOR,
                  APP_USERS.AGENT,
                ]}
              >
                <BulkOperationCardSummaryPage />
              </PrivateRoute>
            ),
          },
          {
            path: ROUTES.BULK_OPERAION.DRIVER_SUMMARY,
            element: (
              <PrivateRoute
                allowedRoles={[
                  APP_USERS.CLIENT,
                  APP_USERS.SUPER_DISTRIBUTOR,
                  APP_USERS.DISTRIBUTOR,
                  APP_USERS.AGENT,
                ]}
              >
                <BulkOperationDriverSummaryPage />
              </PrivateRoute>
            ),
          },
        ],
      },

      // Reporting
      {
        path: ROUTES.REPORTS.INDEX,
        element: (
          <PrivateRoute
            allowedRoles={[
              APP_USERS.CLIENT,
              APP_USERS.SUPER_DISTRIBUTOR,
              APP_USERS.DISTRIBUTOR,
              APP_USERS.AGENT,
            ]}
          >
            <ReportingIndexPage />
          </PrivateRoute>
        ),
        children: [
          {
            path: ROUTES.REPORTS.CARD_LOAD,
            element: (
              <PrivateRoute
                allowedRoles={[
                  APP_USERS.CLIENT,
                  APP_USERS.SUPER_DISTRIBUTOR,
                  APP_USERS.DISTRIBUTOR,
                  APP_USERS.AGENT,
                ]}
              >
                <ReportingCardLoadPage />
              </PrivateRoute>
            ),
          },
          {
            path: ROUTES.REPORTS.PREFUND,
            element: (
              <PrivateRoute
                allowedRoles={[
                  APP_USERS.CLIENT,
                  APP_USERS.SUPER_DISTRIBUTOR,
                  APP_USERS.DISTRIBUTOR,
                  APP_USERS.AGENT,
                ]}
              >
                <ReportingPrefundPage />
              </PrivateRoute>
            ),
          },
          {
            path: ROUTES.REPORTS.LEDGER,
            element: (
              <PrivateRoute
                allowedRoles={[
                  APP_USERS.CLIENT,
                  APP_USERS.SUPER_DISTRIBUTOR,
                  APP_USERS.DISTRIBUTOR,
                  APP_USERS.AGENT,
                ]}
              >
                <ReportingLedgerPage />
              </PrivateRoute>
            ),
          },
          {
            path: ROUTES.REPORTS.B2B,
            element: (
              <PrivateRoute
                allowedRoles={[
                  APP_USERS.CLIENT,
                  APP_USERS.SUPER_DISTRIBUTOR,
                  APP_USERS.DISTRIBUTOR,
                  APP_USERS.AGENT,
                ]}
              >
                <ReportingB2BPage />
              </PrivateRoute>
            ),
          },
          {
            path: ROUTES.REPORTS.DRIVER_PAYMENT_REQ,
            element: (
              <PrivateRoute
                allowedRoles={[
                  APP_USERS.CLIENT,
                  APP_USERS.SUPER_DISTRIBUTOR,
                  APP_USERS.DISTRIBUTOR,
                  APP_USERS.AGENT,
                ]}
              >
                <ReportingDriverPaymentReqPage />
              </PrivateRoute>
            ),
          },
        ],
      },

      // Requests
      {
        path: ROUTES.REQUESTS.INDEX,
        element: (
          <Suspense fallback={<SkeletonPage />}>
            <PrivateRoute
              allowedRoles={[
                APP_USERS.CLIENT,
                APP_USERS.SUPER_DISTRIBUTOR,
                APP_USERS.DISTRIBUTOR,
                APP_USERS.AGENT,
              ]}
            >
              <RequestsIndexPage />
            </PrivateRoute>
          </Suspense>
        ),
        children: [
          {
            path: ROUTES.REQUESTS.CARD_LOAD,
            element: (
              <PrivateRoute
                allowedRoles={[
                  APP_USERS.CLIENT,
                  APP_USERS.SUPER_DISTRIBUTOR,
                  APP_USERS.DISTRIBUTOR,
                ]}
              >
                <RequestsCardLoadPage />
              </PrivateRoute>
            ),
          },
          {
            path: ROUTES.REQUESTS.PREFUND,
            element: (
              <PrivateRoute
                allowedRoles={[
                  APP_USERS.CLIENT,
                  APP_USERS.SUPER_DISTRIBUTOR,
                  APP_USERS.DISTRIBUTOR,
                ]}
              >
                <RequestsPrefundPage />
              </PrivateRoute>
            ),
          },
          {
            path: ROUTES.REQUESTS.B2B,
            element: (
              <PrivateRoute
                allowedRoles={[
                  APP_USERS.CLIENT,
                  APP_USERS.SUPER_DISTRIBUTOR,
                  APP_USERS.DISTRIBUTOR,
                ]}
              >
                <RequestsB2BPage />
              </PrivateRoute>
            ),
          },
          {
            path: ROUTES.REQUESTS.DRIVER_PAYMENT_REQ,
            element: (
              <PrivateRoute
                allowedRoles={[
                  APP_USERS.CLIENT,
                  APP_USERS.SUPER_DISTRIBUTOR,
                  APP_USERS.DISTRIBUTOR,
                  APP_USERS.AGENT,
                ]}
              >
                <RequestsDriverPaymentReqPage />
              </PrivateRoute>
            ),
          },
        ],
      },

      // wps
      {
        path: ROUTES.WPS.INDEX,
        element: <Outlet />,
        children: [
          {
            path: ROUTES.WPS.INDEX,
            element: (
              <Suspense fallback={<SkeletonPage />}>
                <PrivateRoute
                  allowedRoles={[APP_USERS.CLIENT, APP_USERS.SUPER_DISTRIBUTOR]}
                >
                  <WpsPage />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: ROUTES.WPS.RECENT_PAID_SALARY,
            element: (
              <Suspense fallback={<Spinner text={"Loading.."} />}>
                <PrivateRoute
                  allowedRoles={[APP_USERS.CLIENT, APP_USERS.SUPER_DISTRIBUTOR]}
                >
                  <RecentlyPaidSalaryPage />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: ROUTES.WPS.EMPLOYEE_LIST,
            element: (
              <Suspense fallback={<Spinner text={"Loading.."} />}>
                <PrivateRoute
                  allowedRoles={[APP_USERS.CLIENT, APP_USERS.SUPER_DISTRIBUTOR]}
                >
                  <EmployeePage />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: ROUTES.WPS.GENERATE_SALARY,
            element: (
              <Suspense fallback={<Spinner text={"Loading.."} />}>
                <PrivateRoute
                  allowedRoles={[APP_USERS.CLIENT, APP_USERS.SUPER_DISTRIBUTOR]}
                >
                  <EmployerGenerateSalaryPage />
                </PrivateRoute>
              </Suspense>
            ),
          },
          {
            path: `${ROUTES.WPS.GENERATE_SALARY}/:refNumber`,
            element: (
              <Suspense fallback={<Spinner text={"Loading.."} />}>
                <PrivateRoute
                  allowedRoles={[APP_USERS.CLIENT, APP_USERS.SUPER_DISTRIBUTOR]}
                >
                  <EmployerGenerateSalaryPage />
                </PrivateRoute>
              </Suspense>
            ),
          },
        ],
      },

      // Store
      {
        path: ROUTES.STORE.INDEX,
        element: (
          <Suspense fallback={<SkeletonPage />}>
            <StoreIndexPage />
          </Suspense>
        ),
        children: [
          {
            path: ROUTES.STORE.HOME,
            element: <StoreHomePage />,
          },
          {
            path: ROUTES.STORE.PRODUCTS,
            element: <StoreProductsPage />,
          },
          {
            path: ROUTES.STORE.PRODUCT,
            element: <StoreProductPage />,
          },
          {
            path: ROUTES.STORE.CART,
            element: <StoreCartPage />,
          },
          {
            path: ROUTES.STORE.ORDERS,
            element: <OrderHistoryPage />,
          },
          {
            path: ROUTES.STORE.ORDER_VIEW,
            element: <OrderDetailPage />,
          },
        ],
      },

      // Settings
      {
        path: ROUTES.SETTINGS.INDEX,
        element: (
          <Suspense fallback={<Spinner text={"Loading.."} />}>
            <PrivateRoute
              allowedRoles={[
                APP_USERS.CLIENT,
                APP_USERS.SUPER_DISTRIBUTOR,
                APP_USERS.DISTRIBUTOR,
                APP_USERS.AGENT,
              ]}
            >
              <SettingsIndexPage />
            </PrivateRoute>
          </Suspense>
        ),
        children: [
          {
            path: ROUTES.SETTINGS.USERS,
            element: (
              <PrivateRoute
                allowedRoles={[
                  APP_USERS.CLIENT,
                  APP_USERS.SUPER_DISTRIBUTOR,
                  APP_USERS.DISTRIBUTOR,
                ]}
              >
                <SettingsUsersPage />
              </PrivateRoute>
            ),
          },
          {
            path: ROUTES.SETTINGS.SECURITY,
            element: (
              <PrivateRoute
                allowedRoles={[
                  APP_USERS.CLIENT,
                  APP_USERS.SUPER_DISTRIBUTOR,
                  APP_USERS.DISTRIBUTOR,
                  APP_USERS.AGENT,
                ]}
              >
                <SettingsSecurityPage />
              </PrivateRoute>
            ),
          },
          {
            path: ROUTES.SETTINGS.USAGE_REPORT,
            element: (
              <PrivateRoute
                allowedRoles={[
                  APP_USERS.CLIENT,
                  APP_USERS.SUPER_DISTRIBUTOR,
                  APP_USERS.DISTRIBUTOR,
                ]}
              >
                <SettingsUsageReportPage />
              </PrivateRoute>
            ),
          },
          {
            path: ROUTES.SETTINGS.HELP,
            element: (
              <PrivateRoute
                allowedRoles={[
                  APP_USERS.CLIENT,
                  APP_USERS.SUPER_DISTRIBUTOR,
                  APP_USERS.DISTRIBUTOR,
                  APP_USERS.AGENT,
                ]}
              >
                <SettingsHelpPage />
              </PrivateRoute>
            ),
          },
          {
            path: ROUTES.SETTINGS.ACCOUNT_DETAILS,
            element: (
              <PrivateRoute
                allowedRoles={[
                  APP_USERS.CLIENT,
                  APP_USERS.SUPER_DISTRIBUTOR,
                  APP_USERS.DISTRIBUTOR,
                  APP_USERS.AGENT,
                ]}
              >
                <SettingsAccountsPage />
              </PrivateRoute>
            ),
          },
          {
            path: ROUTES.SETTINGS.EXPENSE_MANAGEMENT,
            element: (
              <PrivateRoute
                allowedRoles={[
                  APP_USERS.CLIENT,
                  APP_USERS.SUPER_DISTRIBUTOR,
                  APP_USERS.DISTRIBUTOR,
                  APP_USERS.AGENT,
                ]}
              >
                <RequestsExpenseManagementPage />
              </PrivateRoute>
            ),
          },
        ],
      },
    ],
  },
]);

export const AppRouter = browserRouter;
